<!-- CrudPedidosSeguimiento.vue — Vista de seguimiento para Tere (Laser) -->
<template>
  <div class="crud-container" data-aos="fade-right">
    <!-- Encabezado -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="titulo-seccion m-0">Seguimiento de pedidos</h3>
    </div>

    <!-- Filtros -->
    <PedidosFiltros
      v-model:filtro="filtro"
      v-model:filtroEstatus="filtroEstatus"
      v-model:filtroPrioridad="filtroPrioridad"
    />

    <!-- Tabla -->
    <PedidosTablaS
      :pedidos="pedidosPaginados"
      :loading="loading"
      :paginaActual="paginaActual"
      :totalPaginas="totalPaginas"
      :totalRegistros="pedidosOrdenados.length"
      :porPagina="porPagina"
      :opcionesPorPagina="paginacionStore.opcionesPorPagina"
      :rangoInicio="rangoInicio"
      :rangoFin="rangoFin"
      :ordenCampo="ordenCampo"
      :ordenAsc="ordenAsc"
      @ver-detalle="$refs.modalDetalle.abrir($event)"
      @ver-proyectos="$refs.modalProyectos.abrir($event)"
      @ordenar="handleOrdenar"
      @cambiar-pagina="cambiarPagina"
      @cambiar-por-pagina="cambiarPorPagina"
    />

    <!-- Modal detalle (solo lectura) -->
    <ModalDetallePedido ref="modalDetalle" />

    <!-- Modal proyectos PDF -->
    <ModalProyectosPedido ref="modalProyectos" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Swal from "sweetalert2";
import { getPedidos } from "@/utils/api_backend";
import { usePaginacionSeguimientoStore } from "@/store/paginacionStore";

import PedidosFiltros from "../SeguimientoLaser/PedidosFiltros.vue";
import PedidosTablaS from "./PedidosTabla.vue";
import ModalDetallePedido from "../Pedidos/ModalDetallePedido.vue";
import ModalProyectosPedido from "../SeguimientoLaser/ModalProyectosPedido.vue";

// ── Store paginación (persiste preferencia de filas) ──────
const paginacionStore = usePaginacionSeguimientoStore();

// ── Estado ───────────────────────────────────────────────
const pedidos = ref([]);
const loading = ref(false);

// ── Filtros ──────────────────────────────────────────────
const filtro = ref("");
const filtroEstatus = ref("");
const filtroPrioridad = ref("");

// ── Ordenamiento ─────────────────────────────────────────
const ordenCampo = ref(""); // vacío = orden natural (por peso + días)
const ordenAsc = ref(true);

// ── Paginación ────────────────────────────────────────────
const paginaActual = ref(1);
const porPagina = computed(() => paginacionStore.porPagina);

// Resetear página al filtrar u ordenar
watch([filtro, filtroEstatus, filtroPrioridad, ordenCampo, ordenAsc], () => {
  paginaActual.value = 1;
});

// ── Filtrado ─────────────────────────────────────────────
const pedidosFiltrados = computed(() =>
  pedidos.value.filter((p) => {
    const q = filtro.value.toLowerCase();
    const matchTexto =
      String(p.numero_pedido).includes(q) ||
      p.cliente?.toLowerCase().includes(q) ||
      p.cliente_numero?.toLowerCase().includes(q);
    const matchEstatus =
      !filtroEstatus.value || p.estatus_almacen === filtroEstatus.value;
    const matchPrioridad =
      !filtroPrioridad.value || p.prioridad === filtroPrioridad.value;
    return matchTexto && matchEstatus && matchPrioridad;
  })
);

// ── Ordenamiento ─────────────────────────────────────────
const PESO_PRIORIDAD = { Alta: 1, Media: 2, Baja: 3 };

const pedidosOrdenados = computed(() => {
  const lista = [...pedidosFiltrados.value];

  // Si no hay columna de orden activa → orden natural (peso grupo + días restantes)
  if (!ordenCampo.value) {
    return lista.sort((a, b) => {
      const peso = (p) => {
        if (p.estatus_almacen === "Cancelado") return 3;
        if (p.estatus_almacen === "Entregado") return 2;
        return 1;
      };
      const pa = peso(a),
        pb = peso(b);
      if (pa !== pb) return pa - pb;
      if (a.dias_restantes == null && b.dias_restantes == null) return 0;
      if (a.dias_restantes == null) return 1;
      if (b.dias_restantes == null) return -1;
      return a.dias_restantes - b.dias_restantes;
    });
  }

  // Orden por columna seleccionada
  return lista.sort((a, b) => {
    let valA = a[ordenCampo.value];
    let valB = b[ordenCampo.value];

    // Tratamiento especial para prioridad (Alta > Media > Baja)
    if (ordenCampo.value === "prioridad") {
      valA = PESO_PRIORIDAD[valA] ?? 9;
      valB = PESO_PRIORIDAD[valB] ?? 9;
    }

    // Nulos al final
    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;

    if (typeof valA === "string") {
      valA = valA.toLowerCase();
      valB = (valB ?? "").toLowerCase();
    }

    if (valA < valB) return ordenAsc.value ? -1 : 1;
    if (valA > valB) return ordenAsc.value ? 1 : -1;
    return 0;
  });
});

// ── Paginación computeds ──────────────────────────────────
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(pedidosOrdenados.value.length / porPagina.value))
);
const rangoInicio = computed(() =>
  pedidosOrdenados.value.length === 0 ? 0 : (paginaActual.value - 1) * porPagina.value + 1
);
const rangoFin = computed(() =>
  Math.min(paginaActual.value * porPagina.value, pedidosOrdenados.value.length)
);
const pedidosPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return pedidosOrdenados.value.slice(inicio, inicio + porPagina.value);
});

// ── Handlers ─────────────────────────────────────────────
function handleOrdenar(campo) {
  if (ordenCampo.value === campo) {
    ordenAsc.value = !ordenAsc.value;
  } else {
    ordenCampo.value = campo;
    ordenAsc.value = true;
  }
}
function cambiarPagina(pagina) {
  if (pagina >= 1 && pagina <= totalPaginas.value) paginaActual.value = pagina;
}
function cambiarPorPagina(n) {
  paginacionStore.setPorPagina(n);
  paginaActual.value = 1;
}

// ── Carga ─────────────────────────────────────────────────
async function cargarPedidos() {
  loading.value = true;
  try {
    pedidos.value = await getPedidos();
  } catch {
    Swal.fire("Error", "No se pudieron cargar los pedidos.", "error");
  } finally {
    loading.value = false;
  }
}

onMounted(cargarPedidos);
</script>

<style scoped>
.titulo-seccion {
  color: var(--d-cream);
  font-weight: 800;
}
</style>
